/* eslint-disable jsx-a11y/media-has-caption */
import React, { useCallback, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Checkbox from '@components/Form/Checkbox';
import SortableButton from '@components/Albums/TableView/SortableButton';
import { createToast, TOAST_TYPES } from '@components/Toast';
import AlbumDownloadTooltipBody from '@components/AlbumDownloadTooltipBody';
import Tooltip from '@components/Tooltip';
import { Table, Th, Tr, Td } from '@components/Albums/TableView/style';
import { ActionButton } from '@styles/globalStyles';
import { getAlbumBatchOptions, stemBatchOptions } from '@app/consts';

const TableView = ({
  musicStore: { albums, order, setSort, downloadAlbum },
  moveToAlbum,
  selectedAlbums,
  submitForm,
  stems,
}) => {
  const orderBy = useCallback(by => (order.by === by ? order.dir : ''), [
    order,
  ]);
  const playerRef = useRef(null);

  const onPlay = e => {
    if (playerRef.current) {
      playerRef.current.pause();
    }

    playerRef.current = e.target;
  };

  return (
    <Table hasSelectedAlbums={!!selectedAlbums.length}>
      <thead>
        <tr>
          <Th>&nbsp;</Th>
          <Th>Cover</Th>
          <Th>
            <SortableButton
              text="Name"
              order={orderBy('name')}
              onClick={() => setSort('name')}
            />
          </Th>
          <Th>Medley</Th>
          <Th>
            <SortableButton
              text="Release Date"
              order={orderBy('release_date')}
              onClick={() => setSort('release_date')}
            />
          </Th>
          <Th>
            <SortableButton
              text="Directory Name"
              order={orderBy('directory_name')}
              onClick={() => setSort('directory_name')}
            />
          </Th>
          <Th>
            <SortableButton
              text="Last Downloaded"
              order={orderBy('last_download')}
              onClick={() => setSort('last_download')}
            />
          </Th>
          <Th>&nbsp;</Th>
        </tr>
      </thead>
      <tbody>
        {albums.map(item => {
          const downloadOptions = stems
            ? stemBatchOptions
            : getAlbumBatchOptions(item);

          return (
            <Tr
              key={`albums-table-row_${item.id}`}
              onClick={() => moveToAlbum(item.uuid)}
            >
              <Td>
                <Checkbox
                  name="albums"
                  value={item.uuid}
                  onClick={ev => {
                    ev.stopPropagation();
                    if (selectedAlbums.length > 5) {
                      createToast(
                        "You can't select more than 10 albums in one batch.",
                        TOAST_TYPES.INFO,
                      );
                    }
                  }}
                  onChange={() => {
                    if (selectedAlbums.length <= 5) {
                      submitForm();
                    }

                    return false;
                  }}
                  disabled={
                    selectedAlbums.length >= 5 &&
                    !selectedAlbums.includes(item.uuid)
                  }
                />
              </Td>
              <Td cover>
                <img src={item.cover.url} alt={item.name} />
              </Td>
              <Td>{item.name}</Td>
              <Td>
                {item.medley.url ? (
                  <audio
                    controls
                    src={item.medley.url}
                    preload="none"
                    onPlay={onPlay}
                  >
                    Your browser does not support the audio element.
                  </audio>
                ) : null}
              </Td>
              <Td>{item.release_date}</Td>
              <Td>{item.directory_name}</Td>
              <Td>
                {item.last_download &&
                  dayjs(item.last_download).format('YYYY-MM-DD HH:mm')}
              </Td>
              <Td>
                <Tooltip
                  tooltipBody={
                    <AlbumDownloadTooltipBody
                      options={downloadOptions}
                      onSelect={(type, value) => {
                        downloadAlbum(item, { type, value });
                      }}
                      id={item.uuid}
                    />
                  }
                >
                  <ActionButton
                    as="button"
                    type="button"
                    onClick={ev => ev.stopPropagation()}
                  >
                    <img src="/images/icons/download.svg" alt="download" />
                  </ActionButton>
                </Tooltip>
              </Td>
            </Tr>
          );
        })}
      </tbody>
    </Table>
  );
};

TableView.propTypes = {
  moveToAlbum: PropTypes.func.isRequired,
  selectedAlbums: PropTypes.array.isRequired,
  submitForm: PropTypes.func.isRequired,
};

export default inject('musicStore')(observer(TableView));
